/** Sons do atendimento + avisos do sistema (Notification API + Service Worker). */

export const CHAT_NOTIFY_STORAGE_KEY = 'aurax_chat_notify_prefs';
export const CHAT_NOTIFY_BANNER_KEY = 'aurax_chat_notify_banner_dismissed';

export const NOTIFY_KIND = {
  CHAT_MESSAGE: 'chat.message',
  CHAT_SYSTEM: 'chat.system',
  CHAT_ESCALATED: 'chat.escalated',
  CHAT_ESCALATION_RESOLVED: 'chat.escalation_resolved',
  TEAM_ATTENDANCE: 'team.attendance',
  COMMERCE_CREDIT_SALE: 'commerce.credit_sale',
  COMMERCE_CREDIT_PURCHASE: 'commerce.credit_purchase',
  COMMERCE_RENEWAL: 'commerce.renewal',
  COMMERCE_PIX: 'commerce.pix',
  COMMERCE_BILLING_DAY: 'commerce.billing_day',
  RESELLER_STATUS: 'reseller.status',
  TEST: 'ops.test',
};

const DEFAULT_PREFS = {
  soundsEnabled: true,
  soundReceive: true,
  soundSend: true,
  soundAlert: true,
  desktopPush: true,
  notifyChat: true,
  notifyTeam: true,
  notifyCommerce: true,
};

const SW_PATH = '/aurax-notify-sw.js';
const listeners = new Set();
let audioCtx = null;
let lastInboundSoundAt = 0;
let unlockInstalled = false;
let swRegisterPromise = null;
let swControllerGuard = false;

function canUseStorage() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

export function loadNotifyPrefs() {
  if (!canUseStorage()) return { ...DEFAULT_PREFS };
  try {
    const raw = window.localStorage.getItem(CHAT_NOTIFY_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_PREFS };
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_PREFS, ...(parsed && typeof parsed === 'object' ? parsed : {}) };
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

export function saveNotifyPrefs(partial) {
  const next = { ...loadNotifyPrefs(), ...partial };
  try {
    window.localStorage.setItem(CHAT_NOTIFY_STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* ignore quota */
  }
  listeners.forEach((cb) => {
    try {
      cb(next);
    } catch {
      /* ignore */
    }
  });
  return next;
}

export function subscribeNotifyPrefs(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function getAudioCtx() {
  if (typeof window === 'undefined') return null;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!audioCtx) audioCtx = new AC();
  return audioCtx;
}

export function unlockChatAudio() {
  try {
    const ctx = getAudioCtx();
    if (ctx && ctx.state === 'suspended') ctx.resume().catch(() => {});
  } catch {
    /* ignore */
  }
}

export function installChatAudioUnlock() {
  if (unlockInstalled || typeof window === 'undefined') return;
  unlockInstalled = true;
  const unlock = () => unlockChatAudio();
  window.addEventListener('pointerdown', unlock, { passive: true });
  window.addEventListener('keydown', unlock);
  window.addEventListener('touchstart', unlock, { passive: true });
}

function playTone(ctx, { freq, start, dur, gain = 0.12, type = 'sine', freqEnd }) {
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = 3200;
  osc.type = type;
  osc.frequency.setValueAtTime(freq, start);
  if (freqEnd && freqEnd > 0) {
    osc.frequency.exponentialRampToValueAtTime(Math.max(40, freqEnd), start + dur);
  }
  g.gain.setValueAtTime(0.0001, start);
  g.gain.exponentialRampToValueAtTime(Math.max(0.0002, gain), start + 0.014);
  g.gain.exponentialRampToValueAtTime(0.0001, start + dur);
  osc.connect(filter);
  filter.connect(g);
  g.connect(ctx.destination);
  osc.start(start);
  osc.stop(start + dur + 0.02);
}

/**
 * @param {'receive'|'send'|'alert'} kind
 */
export function playChatSound(kind = 'receive') {
  const prefs = loadNotifyPrefs();
  if (!prefs.soundsEnabled) return;
  if (kind === 'receive' && !prefs.soundReceive) return;
  if (kind === 'send' && !prefs.soundSend) return;
  if (kind === 'alert' && !prefs.soundAlert) return;

  if (kind === 'receive') {
    const now = Date.now();
    if (now - lastInboundSoundAt < 450) return;
    lastInboundSoundAt = now;
  }

  try {
    const ctx = getAudioCtx();
    if (!ctx) return;
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});
    const t0 = ctx.currentTime + 0.01;
    if (kind === 'send') {
      playTone(ctx, { freq: 1260, start: t0, dur: 0.055, gain: 0.055, type: 'triangle' });
      playTone(ctx, { freq: 1880, start: t0 + 0.018, dur: 0.045, gain: 0.035, type: 'sine' });
      return;
    }
    if (kind === 'receive') {
      playTone(ctx, { freq: 880, start: t0, dur: 0.13, gain: 0.13, type: 'sine' });
      playTone(ctx, { freq: 1174.66, start: t0 + 0.08, dur: 0.2, gain: 0.15, type: 'sine' });
      try {
        navigator.vibrate?.(28);
      } catch {
        /* ignore */
      }
      return;
    }
    playTone(ctx, { freq: 659.25, start: t0, dur: 0.14, gain: 0.13, type: 'sine' });
    playTone(ctx, { freq: 783.99, start: t0 + 0.11, dur: 0.15, gain: 0.13, type: 'sine' });
    playTone(ctx, { freq: 1046.5, start: t0 + 0.22, dur: 0.22, gain: 0.15, type: 'sine' });
    try {
      navigator.vibrate?.([24, 40, 32]);
    } catch {
      /* ignore */
    }
  } catch {
    /* ignore autoplay */
  }
}

export function notificationPermission() {
  if (typeof window === 'undefined') return 'unsupported';
  if (!window.isSecureContext) return 'unsupported';
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission;
}

function originBase() {
  try {
    return window.location.origin;
  } catch {
    return '';
  }
}

/** Ícone da marca (badge / fallback geral). */
export function notifyBrandIconUrl() {
  try {
    // Preferir ícone PWA do Office (se applyPwaBrand já correu).
    // eslint-disable-next-line global-require
    const { getPwaIconUrl } = require('./pwaInstall');
    return getPwaIconUrl();
  } catch {
    return `${originBase()}/pwa-icon-192.png`;
  }
}

/** Ícone de perfil padrão (sem foto do contato). */
export function notifyProfileIconUrl() {
  return `${originBase()}/notify-profile.png`;
}

export function notifyTeamIconUrl() {
  return `${originBase()}/notify-team.png`;
}

export function notifyCommerceIconUrl() {
  return `${originBase()}/notify-commerce.png`;
}

/** @deprecated use notifyBrandIconUrl */
function notifyIconUrl() {
  return notifyBrandIconUrl();
}

export function absoluteNotifyUrl(url) {
  const raw = String(url || '').trim();
  if (!raw) return '';
  if (raw.startsWith('http://') || raw.startsWith('https://') || raw.startsWith('data:')) return raw;
  if (raw.startsWith('/')) return `${originBase()}${raw}`;
  return raw;
}

function kindFamily(kind) {
  const k = String(kind || '');
  if (k.startsWith('chat.') || k === NOTIFY_KIND.TEST) return 'chat';
  if (k.startsWith('team.') || k.startsWith('reseller.')) return 'team';
  if (k.startsWith('commerce.')) return 'commerce';
  return 'chat';
}

function prefsAllowKind(kind, prefs) {
  if (!prefs.desktopPush) return false;
  const fam = kindFamily(kind);
  if (fam === 'chat') return prefs.notifyChat !== false;
  if (fam === 'team') return prefs.notifyTeam !== false;
  if (fam === 'commerce') return prefs.notifyCommerce !== false;
  return true;
}

function formatMoneyBRL(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  try {
    return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  } catch {
    return `R$ ${n.toFixed(2)}`;
  }
}

function maskPhone(phone) {
  const d = String(phone || '').replace(/\D/g, '');
  if (d.length < 8) return '';
  let national = d;
  if (national.startsWith('55') && national.length >= 12) national = national.slice(2);
  const ddd = national.length >= 10 ? national.slice(0, 2) : '';
  const rest = ddd ? national.slice(2) : national;
  if (rest.length >= 8 && ddd) {
    return `(${ddd}) ${rest.slice(0, Math.min(5, rest.length - 4))}…-${rest.slice(-4)}`;
  }
  return `…${d.slice(-4)}`;
}

function clockNow() {
  try {
    return new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

function joinNotifyLines(lines) {
  return lines.filter(Boolean).join('\n');
}

const ATTENDANCE_META = {
  available: { emoji: '🟢', label: 'Online', tone: 'disponível para atendimento' },
  lunch: { emoji: '🟡', label: 'Almoço', tone: 'em pausa (almoço)' },
  dinner: { emoji: '🟡', label: 'Jantar', tone: 'em pausa (jantar)' },
  bathroom: { emoji: '🟡', label: 'Banheiro', tone: 'em pausa rápida' },
  break: { emoji: '🟡', label: 'Pausa', tone: 'em intervalo' },
  unavailable: { emoji: '🔴', label: 'Indisponível', tone: 'temporariamente indisponível' },
  off: { emoji: '⚫', label: 'Offline', tone: 'encerrou o expediente' },
  sick: { emoji: '🔴', label: 'Afastado', tone: 'marcado como afastado' },
};

/**
 * Monta título/corpo/ícone profissionais a partir do kind.
 * @returns {{ title: string, body: string, icon: string, badge: string, url: string, tag: string, sound: 'receive'|'send'|'alert'|'none' }}
 */
export function buildOfficeNotifyContent(input = {}) {
  const kind = input.kind || NOTIFY_KIND.CHAT_MESSAGE;
  const name = String(input.contactName || input.title || '').trim();
  const snippet = String(input.body || '').replace(/\s+/g, ' ').trim();
  const phone = maskPhone(input.contactPhone);
  const time = clockNow();
  const brand = notifyBrandIconUrl();
  const profile = absoluteNotifyUrl(input.iconUrl) || notifyProfileIconUrl();
  const money = formatMoneyBRL(input.amount);

  if (kind === NOTIFY_KIND.CHAT_MESSAGE || kind === 'receive') {
    const msg = snippet ? snippet.slice(0, 220) : '';
    return {
      title: `💬 ${name || 'Nova mensagem'}`,
      body: joinNotifyLines([
        msg && `📩 ${msg}`,
        phone && `📱 ${phone}`,
        input.conversationId && `🗂️ Atendimento #${String(input.conversationId).slice(-6)}`,
        time && `🕐 ${time}`,
        '👉 Toque para abrir a conversa',
      ]) || 'Nova mensagem no atendimento.',
      icon: profile,
      badge: brand,
      url: input.url || `/chats${input.conversationId ? `?open=${encodeURIComponent(String(input.conversationId))}` : ''}`,
      tag: input.tag || `aurax-chat-${input.conversationId || Date.now()}`,
      sound: 'receive',
    };
  }

  if (kind === NOTIFY_KIND.CHAT_SYSTEM || kind === 'alert') {
    return {
      title: `🔔 ${name || 'Aviso do sistema'}`,
      body: joinNotifyLines([
        snippet && `📋 ${snippet.slice(0, 220)}`,
        phone && `📱 ${phone}`,
        input.conversationId && `🗂️ Atendimento #${String(input.conversationId).slice(-6)}`,
        time && `🕐 ${time}`,
        '👉 Toque para abrir',
      ]) || 'Actualização no atendimento.',
      icon: profile,
      badge: brand,
      url: input.url || `/chats${input.conversationId ? `?open=${encodeURIComponent(String(input.conversationId))}` : ''}`,
      tag: input.tag || `aurax-sys-${input.conversationId || Date.now()}`,
      sound: 'alert',
    };
  }

  if (kind === NOTIFY_KIND.CHAT_ESCALATED) {
    return {
      title: '🚨 Conversa escalada — precisa de atenção',
      body: joinNotifyLines([
        `👤 Cliente: ${input.contactName || '—'}`,
        `🧑‍💼 Escalado por: ${input.actorName || 'Atendente'}`,
        input.reason && `📌 Motivo: ${input.reason}`,
        input.conversationId && `🗂️ Atendimento #${String(input.conversationId).slice(-6)}`,
        time && `🕐 ${time}`,
        '👉 Abrir fila de escalados',
      ]),
      icon: notifyTeamIconUrl(),
      badge: brand,
      url: input.url || '/chats?tab=escalated',
      tag: input.tag || `aurax-esc-${input.conversationId || Date.now()}`,
      sound: 'alert',
    };
  }

  if (kind === NOTIFY_KIND.CHAT_ESCALATION_RESOLVED) {
    return {
      title: '✅ Escalação resolvida',
      body: joinNotifyLines([
        `👤 Cliente: ${input.contactName || '—'}`,
        `🧑‍💼 Resolvido por: ${input.actorName || 'Admin'}`,
        input.conversationId && `🗂️ Atendimento #${String(input.conversationId).slice(-6)}`,
        time && `🕐 ${time}`,
      ]),
      icon: notifyTeamIconUrl(),
      badge: brand,
      url: input.url || '/chats',
      tag: input.tag || `aurax-esc-ok-${input.conversationId || Date.now()}`,
      sound: 'alert',
    };
  }

  if (kind === NOTIFY_KIND.TEAM_ATTENDANCE) {
    const st = String(input.status || '').toLowerCase();
    const meta = ATTENDANCE_META[st] || { emoji: '👤', label: input.statusLabel || st || 'Estado', tone: 'actualizou o ponto' };
    return {
      title: `${meta.emoji} ${input.attendantName || 'Atendente'} · ${meta.label}`,
      body: joinNotifyLines([
        `📍 Estado: ${meta.tone}`,
        input.note && `📝 Nota: ${String(input.note).slice(0, 120)}`,
        time && `🕐 ${time}`,
        '👉 Abrir monitor de atendentes',
      ]),
      icon: absoluteNotifyUrl(input.iconUrl) || notifyTeamIconUrl(),
      badge: brand,
      url: input.url || '/attendants-monitor',
      tag: input.tag || `aurax-att-${input.attendantId || st}`,
      sound: st === 'available' ? 'receive' : 'alert',
    };
  }

  if (
    kind === NOTIFY_KIND.COMMERCE_CREDIT_SALE ||
    kind === NOTIFY_KIND.COMMERCE_CREDIT_PURCHASE ||
    kind === NOTIFY_KIND.COMMERCE_RENEWAL ||
    kind === NOTIFY_KIND.COMMERCE_PIX ||
    kind === NOTIFY_KIND.COMMERCE_BILLING_DAY
  ) {
    const labels = {
      [NOTIFY_KIND.COMMERCE_CREDIT_SALE]: '💳 Venda de créditos confirmada',
      [NOTIFY_KIND.COMMERCE_CREDIT_PURCHASE]: '🛒 Compra de créditos confirmada',
      [NOTIFY_KIND.COMMERCE_RENEWAL]: '♻️ Renovação confirmada',
      [NOTIFY_KIND.COMMERCE_PIX]: '💠 PIX confirmado',
      [NOTIFY_KIND.COMMERCE_BILLING_DAY]: '📊 Faturamento do dia',
    };
    return {
      title: labels[kind] || '💰 Movimento financeiro',
      body: joinNotifyLines([
        input.actorName && `👤 ${input.actorName}`,
        input.packName && `📦 Pacote: ${input.packName}`,
        input.credits != null && `🪙 Créditos: ${input.credits}`,
        money && `💵 Valor: ${money}`,
        input.dayTotal != null && `📈 Faturamento de hoje: ${formatMoneyBRL(input.dayTotal)}`,
        snippet && `📝 ${snippet.slice(0, 160)}`,
        time && `🕐 ${time}`,
        '👉 Abrir detalhes no painel',
      ]) || 'Pagamento registado no Office.',
      icon: notifyCommerceIconUrl(),
      badge: brand,
      url: input.url || '/buy-credits',
      tag: input.tag || `aurax-pay-${input.entityId || Date.now()}`,
      sound: 'alert',
    };
  }

  if (kind === NOTIFY_KIND.RESELLER_STATUS) {
    return {
      title: `🏪 Revenda · ${input.statusLabel || input.status || 'estado'}`,
      body: joinNotifyLines([
        input.actorName && `👤 ${input.actorName}`,
        snippet && `📋 ${snippet.slice(0, 180)}`,
        time && `🕐 ${time}`,
        '👉 Abrir revendas',
      ]),
      icon: notifyTeamIconUrl(),
      badge: brand,
      url: input.url || '/gestor-xui/resellers',
      tag: input.tag || `aurax-res-${input.entityId || Date.now()}`,
      sound: 'alert',
    };
  }

  // Teste / genérico — formato completo de atendimento
  return {
    title: `💬 ${name || input.title || 'Avisos do Office'}`,
    body: joinNotifyLines([
      `📩 ${snippet || 'Teste concluído. Mensagens, vendas e estados da equipe aparecem aqui com todos os detalhes.'}`,
      phone && `📱 ${phone}`,
      time && `🕐 ${time}`,
      '👉 Toque para abrir o painel',
    ]),
    icon: profile,
    badge: brand,
    url: input.url || '/chats',
    tag: input.tag || `aurax-test-${Date.now()}`,
    sound: 'alert',
  };
}

/** Regista o SW de avisos (necessário no Chrome Android / background). */
export function ensureNotifyServiceWorker() {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return Promise.resolve(null);
  }
  if (!window.isSecureContext) return Promise.resolve(null);
  if (!swRegisterPromise) {
    swRegisterPromise = navigator.serviceWorker
      .register(SW_PATH, { scope: '/', updateViaCache: 'none' })
      .then(async (reg) => {
        try {
          await navigator.serviceWorker.ready;
        } catch {
          /* ignore */
        }
        return reg;
      })
      .catch(() => null);
    if (typeof navigator.serviceWorker.addEventListener === 'function' && !swControllerGuard) {
      swControllerGuard = true;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        /* Não recarregar a página — o SW antigo fazia a UI piscar (F5). */
      });
    }
  }
  return swRegisterPromise;
}

/**
 * Pede permissão (obrigatório no gesto do usuário) e mostra um aviso de teste rico.
 */
export async function requestNotifyPermission({ title, body } = {}) {
  unlockChatAudio();
  if (typeof Notification === 'undefined' || !window.isSecureContext) {
    return 'unsupported';
  }

  await ensureNotifyServiceWorker();

  let result = Notification.permission;
  try {
    if (typeof Notification.requestPermission === 'function') {
      result = await Notification.requestPermission();
    }
  } catch {
    result = Notification.permission || 'denied';
  }

  if (result === 'granted') {
    saveNotifyPrefs({ desktopPush: true });
    await showOfficeDesktopNotification({
      kind: NOTIFY_KIND.TEST,
      title: title || 'Avisos do Office',
      body: body || 'Permissão concedida. Mensagens, vendas e estados da equipe aparecem aqui.',
      force: true,
    });
  } else {
    saveNotifyPrefs({ desktopPush: false });
  }
  return result;
}

/**
 * Mostra aviso na barra do sistema (desktop + mobile via Service Worker).
 * Aceita o formato antigo (title/body/conversationId) ou o tipado (kind + campos).
 */
export async function showOfficeDesktopNotification(opts = {}) {
  const prefs = loadNotifyPrefs();
  const kind = opts.kind || (opts.force ? NOTIFY_KIND.TEST : NOTIFY_KIND.CHAT_MESSAGE);
  if (!opts.force) {
    if (!prefs.desktopPush) return { ok: false, error: 'prefs_off' };
    if (!prefsAllowKind(kind, { ...prefs, desktopPush: true })) {
      return { ok: false, error: 'prefs_off' };
    }
  }
  if (typeof Notification === 'undefined' || !window.isSecureContext) {
    return { ok: false, error: 'unsupported' };
  }
  if (Notification.permission !== 'granted') {
    return { ok: false, error: 'permission' };
  }

  const built = buildOfficeNotifyContent({
    ...opts,
    kind,
    contactName: opts.contactName || (kind?.startsWith?.('chat.') ? opts.title : undefined),
    body: opts.body,
    iconUrl: opts.iconUrl || opts.contactProfilePicture,
  });

  const displayTitle = built.title;
  const openUrl = built.url.startsWith('http') ? built.url : `${originBase()}${built.url.startsWith('/') ? '' : '/'}${built.url}`;
  const conversationId = opts.conversationId != null ? String(opts.conversationId) : '';

  const payload = {
    body: String(built.body || '').slice(0, 450),
    tag: built.tag,
    lang: typeof document !== 'undefined' ? document.documentElement.lang || 'pt-BR' : 'pt-BR',
    icon: built.icon,
    badge: built.badge,
    silent: false,
    renotify: true,
    requireInteraction: true,
    data: {
      kind,
      conversationId,
      url: openUrl,
    },
  };

  const onClickNavigate = () => {
    try {
      window.focus();
    } catch {
      /* ignore */
    }
    if (conversationId) {
      try {
        window.sessionStorage.setItem('aurax_open_chat', conversationId);
      } catch {
        /* ignore */
      }
      window.dispatchEvent(
        new CustomEvent('aurax:open-chat', { detail: { conversationId } }),
      );
    }
    if (openUrl) {
      try {
        const path = openUrl.replace(originBase(), '') || '/';
        if (path && path !== window.location.pathname + window.location.search) {
          window.dispatchEvent(new CustomEvent('aurax:notify-navigate', { detail: { url: path } }));
        }
      } catch {
        /* ignore */
      }
    }
  };

  const pageHidden =
    typeof document !== 'undefined' && (document.hidden || document.visibilityState === 'hidden');

  // Em segundo plano: preferir Service Worker (mais fiável no mobile).
  // Em primeiro plano: Notification() uma só vez — evita dois toasts duplicados.
  if (pageHidden) {
    try {
      const reg = await ensureNotifyServiceWorker();
      if (reg && typeof reg.showNotification === 'function') {
        await navigator.serviceWorker.ready;
        await reg.showNotification(displayTitle, payload);
        return { ok: true, via: 'service-worker' };
      }
    } catch (err) {
      /* cai para Notification */
    }
  }

  try {
    const n = new Notification(displayTitle, payload);
    n.onclick = () => {
      onClickNavigate();
      try {
        n.close();
      } catch {
        /* ignore */
      }
    };
    return { ok: true, via: 'notification' };
  } catch (err) {
    /* fallback SW */
  }

  try {
    const reg = await ensureNotifyServiceWorker();
    if (reg && typeof reg.showNotification === 'function') {
      await navigator.serviceWorker.ready;
      await reg.showNotification(displayTitle, payload);
      return { ok: true, via: 'service-worker' };
    }
  } catch (err) {
    return { ok: false, error: String(err?.message || err || 'sw_fail') };
  }

  return { ok: false, error: 'failed' };
}

/** Alias legado. */
export async function showChatDesktopNotification(opts = {}) {
  return showOfficeDesktopNotification(opts);
}

/**
 * Som + aviso do sistema para mensagem recebida / alerta de chat.
 */
export function notifyIncomingChat(payload) {
  const legacyKind = payload?.kind === 'alert' ? 'alert' : 'receive';
  const notifyKind =
    payload?.notifyKind ||
    (legacyKind === 'alert' ? NOTIFY_KIND.CHAT_SYSTEM : NOTIFY_KIND.CHAT_MESSAGE);
  const focused =
    typeof document !== 'undefined' &&
    !document.hidden &&
    document.visibilityState !== 'hidden' &&
    document.hasFocus();
  const isOpen = payload?.isOpenThread === true;

  const sound = buildOfficeNotifyContent({ kind: notifyKind }).sound;
  if (sound && sound !== 'none') playChatSound(sound === 'send' ? 'send' : sound);

  // Aviso completo sempre — só omite se a conversa já estiver aberta e a aba em foco.
  if (!(isOpen && focused)) {
    void showOfficeDesktopNotification({
      kind: notifyKind,
      title: payload?.title,
      body: payload?.body,
      contactName: payload?.contactName || payload?.title,
      contactPhone: payload?.contactPhone,
      contactProfilePicture: payload?.contactProfilePicture || payload?.iconUrl,
      iconUrl: payload?.iconUrl || payload?.contactProfilePicture,
      conversationId: payload?.conversationId,
      tag: payload?.tag,
      actorName: payload?.actorName,
      reason: payload?.reason,
      url: payload?.url,
    });
  }
}

/** Dispara aviso tipado (equipe / comércio / etc.). */
export function notifyOfficeEvent(payload) {
  const kind = payload?.kind || NOTIFY_KIND.TEST;
  const built = buildOfficeNotifyContent({ ...payload, kind });
  if (built.sound && built.sound !== 'none') {
    playChatSound(built.sound === 'send' ? 'send' : built.sound);
  }
  void showOfficeDesktopNotification({ ...payload, kind });
}

export function messageNotifySnippet(msg, fallback = '') {
  const text = String(msg?.content || '')
    .replace(/\s+/g, ' ')
    .trim();
  if (text && text !== '\u200b') return text.slice(0, 220);
  if (msg?.media_url || msg?.media_type || msg?.message_type === 'audio' || msg?.message_type === 'image') {
    const mt = String(msg?.media_type || msg?.message_type || '').toLowerCase();
    if (mt.includes('audio') || mt === 'ptt') return '🎤 Áudio';
    if (mt.includes('image') || mt === 'image') return '🖼️ Imagem';
    if (mt.includes('video')) return '🎬 Vídeo';
    if (mt.includes('document') || mt.includes('pdf')) return '📄 Documento';
    return fallback || '📎 Anexo';
  }
  return fallback;
}

let clickBridgeInstalled = false;

/** Liga o listener de clique vindos do Service Worker (uma vez). */
export function installNotifyClickBridge() {
  if (clickBridgeInstalled) return;
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  clickBridgeInstalled = true;
  navigator.serviceWorker.addEventListener('message', (event) => {
    const data = event?.data;
    if (!data || data.type !== 'aurax-notify-click') return;
    const conversationId = data.conversationId != null ? String(data.conversationId) : '';
    const url = data.url != null ? String(data.url) : '';
    if (conversationId) {
      try {
        window.sessionStorage.setItem('aurax_open_chat', conversationId);
      } catch {
        /* ignore */
      }
      window.dispatchEvent(new CustomEvent('aurax:open-chat', { detail: { conversationId } }));
    }
    if (url) {
      window.dispatchEvent(new CustomEvent('aurax:notify-navigate', { detail: { url } }));
    }
  });
}

/** Garante prefs completas quando o browser já autorizou avisos. */
export function ensureFullNotifyPrefs() {
  const current = loadNotifyPrefs();
  if (typeof Notification === 'undefined') return current;
  if (Notification.permission !== 'granted') return current;

  // Preserve explicit user choices across refresh.
  // Only backfill missing/invalid keys for legacy records.
  const patch = {};
  for (const key of ['desktopPush', 'notifyChat', 'notifyTeam', 'notifyCommerce']) {
    if (typeof current[key] !== 'boolean') patch[key] = true;
  }
  if (!Object.keys(patch).length) return current;
  return saveNotifyPrefs(patch);
}

export function bootstrapChatNotify() {
  installChatAudioUnlock();
  installNotifyClickBridge();
  ensureFullNotifyPrefs();
  void ensureNotifyServiceWorker();
}
